import { isAndroid, isIos } from "../shared/platform";
import "./panels.css";

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const SEEN_KEY = "webscan2:install-guide";
let acknowledged = false;
let installPrompt: InstallPrompt | null = null;
const promptListeners = new Set<() => void>();

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPrompt = event as InstallPrompt;
  for (const listener of promptListeners) listener();
});
window.addEventListener("appinstalled", () => {
  acknowledgeGuide();
  installPrompt = null;
  for (const listener of promptListeners) listener();
});

function acknowledgeGuide(): void {
  acknowledged = true;
  try {
    localStorage.setItem(SEEN_KEY, "seen");
  } catch {
    // Browsers without persistent storage still dismiss it for the current tab.
  }
}

export function needsInstallGuide(): boolean {
  if (
    acknowledged ||
    matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone
  )
    return false;
  try {
    return localStorage.getItem(SEEN_KEY) !== "seen";
  } catch {
    return true;
  }
}

/** The callback stays in this click stack so camera start can request gated access. */
export function installContent(root: HTMLElement, onContinue: () => void): void {
  root.classList.add("install-content");
  const badge = document.createElement("div");
  badge.className = "install-mark";
  badge.setAttribute("aria-hidden", "true");
  badge.innerHTML =
    '<svg viewBox="0 0 48 48" fill="none"><path d="M17 9h-5a3 3 0 0 0-3 3v5m22-8h5a3 3 0 0 1 3 3v5M9 31v5a3 3 0 0 0 3 3h5m14 0h5a3 3 0 0 0 3-3v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><rect x="17" y="15" width="14" height="19" rx="2" stroke="currentColor" stroke-width="2"/></svg>';
  const heading = document.createElement("h2");
  heading.className = "install-title";
  heading.textContent = "Make room for the cards.";
  const intro = document.createElement("p");
  intro.className = "help";
  intro.textContent =
    "Add Webscan to your home screen for a camera view with less browser chrome. You can also scan right here.";
  const platform = document.createElement("p");
  platform.className = "section-label";
  platform.textContent = isIos()
    ? "On iPhone & iPad"
    : isAndroid()
      ? "On Android"
      : "In your browser";
  const steps = document.createElement("ol");
  steps.className = "install-steps";
  const instructions = isIos()
    ? [
        "Open this page in Safari.",
        "Open the Share menu (inside More on some layouts) and choose Add to Home Screen.",
        "Enable Open as Web App if shown, tap Add, then open Webscan from your home screen.",
      ]
    : isAndroid()
      ? [
          "Open your browser’s menu.",
          "Choose Add to Home screen, then Install (or Install app).",
          "Open Webscan from your home screen.",
        ]
      : [
          "Look for Install in your browser menu, or File → Add to Dock in Safari on Mac.",
          "Confirm Install or Add if your browser offers it.",
          "Open it from your apps, or keep scanning in this tab.",
        ];
  for (const text of instructions) {
    const step = document.createElement("li");
    step.textContent = text;
    steps.append(step);
  }
  const actions = document.createElement("div");
  actions.className = "install-actions";
  const install = document.createElement("button");
  install.type = "button";
  install.className = "button button--ghost";
  install.textContent = "Install Webscan";
  install.hidden = !installPrompt;
  const note = document.createElement("p");
  note.className = "help";
  note.setAttribute("role", "status");
  promptListeners.add(() => {
    install.hidden = !installPrompt;
  });
  install.addEventListener("click", () => {
    const prompt = installPrompt;
    if (!prompt) return;
    installPrompt = null;
    install.disabled = true;
    void prompt
      .prompt()
      .then(() => prompt.userChoice)
      .then(({ outcome }) => {
        install.hidden = true;
        note.textContent =
          outcome === "accepted"
            ? "Installation requested. You can continue scanning here."
            : "You can install later from your browser’s menu.";
      })
      .catch(() => {
        note.textContent = "Use your browser’s menu to install, or continue scanning here.";
        install.hidden = true;
      });
  });
  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "button button--primary";
  skip.textContent = "Skip & start scanning";
  skip.addEventListener("click", () => {
    acknowledgeGuide();
    onContinue();
  });
  actions.append(install, skip, note);
  root.append(badge, heading, intro, platform, steps, actions);
}
